import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { quickSearchPlayers, QuickPlayerResult } from '../../lib/supabase';
import { trackPageView } from '../../lib/services/pageTrackingService';

function PlayerAvatar({ src, name }: { src: string; name: string }) {
  const [error, setError] = useState(false);
  const initials = name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  if (!error) {
    return (
      <img
        src={src}
        alt={name}
        width={28}
        height={28}
        className="me-2"
        style={{ borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(255,255,255,0.15)' }}
        onError={() => setError(true)}
      />
    );
  }
  return (
    <span
      className="me-2 d-flex align-items-center justify-content-center fw-bold"
      style={{
        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
        background: 'rgba(255,255,255,0.15)', color: 'white', fontSize: '0.6rem',
      }}
    >
      {initials}
    </span>
  );
}

interface Props {
  /** Ferme le menu mobile après navigation (optionnel) */
  onNavigate?: () => void;
  /** Classe CSS supplémentaire sur le container */
  className?: string;
}

export function QuickPlayerSearch({ onNavigate, className = '' }: Props) {
  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const username = useUserStore((state) => state.username);
  const currentUserId = useUserStore((state) => state.user_id);
  const { isStandardFlexible } = useLeagueMode();

  const [term, setTerm] = useState('');
  const [results, setResults] = useState<QuickPlayerResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fermer au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = useCallback(
    async (value: string) => {
      if (value.length < 3) {
        setResults([]);
        setIsOpen(false);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const { data } = await quickSearchPlayers(value, selectedLeague?.id ?? undefined, {
        isStandardFlexible,
        userId: currentUserId,
      });
      setResults(data ?? []);
      setIsOpen(true);
      setHighlightedIndex(-1);
      setIsLoading(false);
    },
    [currentUserId, isStandardFlexible, selectedLeague?.id]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTerm(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length < 3) {
      setResults([]);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  };

  const handleSelect = (player: QuickPlayerResult) => {
    setTerm('');
    setResults([]);
    setIsOpen(false);
    onNavigate?.();
    // Tracker le clic depuis le quick search (non-bloquant)
    trackPageView('quick_search_player');
    if (!username) {
      navigate('/auth');
    }
    navigate(`/players/${player.playerid}`);
  };

  const handleClear = () => {
    setTerm('');
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < results.length) {
        handleSelect(results[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setTerm('');
      setResults([]);
      setIsOpen(false);
    }
  };

  // Grouper les résultats quand une ligue est sélectionnée
  const leaguePlayers = results.filter((r) => r.status === 'league');
  const freeAgentPlayers = results.filter((r) => r.status === 'freeAgent');
  const globalPlayers = results.filter((r) => r.status === 'global');

  // Calculer l'index global d'un joueur dans la liste pour le highlight
  // (utilisé implicitement via leaguePlayers.length + i dans renderItem)

  const renderItem = (player: QuickPlayerResult, flatIndex: number) => {
    const isHighlighted = flatIndex === highlightedIndex;
    const photoUrl = `https://assets.nhle.com/mugs/nhl/20242025/${player.teamabbrevs}/${player.playerid}.png`;
    return (
      <button
        key={`${player.playerid}-${player.status}`}
        className="w-100 d-flex align-items-center px-3 py-2 text-start border-0"
        style={{
          background: isHighlighted ? 'rgba(255,255,255,0.12)' : 'transparent',
          color: 'white',
          cursor: 'pointer',
          transition: 'background 0.1s',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
        onMouseEnter={() => setHighlightedIndex(flatIndex)}
        onMouseLeave={() => setHighlightedIndex(-1)}
        onMouseDown={(e) => {
          e.preventDefault();
          handleSelect(player);
        }}
      >
        {/* Photo du joueur */}
        <PlayerAvatar src={photoUrl} name={player.fullname} />

        {/* Nom */}
        <span className="fw-semibold flex-grow-1" style={{ fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {player.fullname}
        </span>

        {/* Équipe NHL */}
        <span
          className="ms-2 text-end flex-shrink-0"
          style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', minWidth: 32 }}
        >
          {player.teamabbrevs}
        </span>
      </button>
    );
  };

  const renderGroupLabel = (label: string) => (
    <div
      className="px-3 py-1"
      style={{
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        color: 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      {label}
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={`position-relative ${className}`}
      style={{ minWidth: 0 }}
    >
      {/* Input */}
      <div
        className="d-flex align-items-center"
        style={{
          background: 'rgba(255,255,255,0.1)',
          border: `1px solid ${isOpen ? 'rgba(245,158,11,0.6)' : 'rgba(255,255,255,0.2)'}`,
          borderRadius: isOpen && results.length > 0 ? '10px 10px 0 0' : '10px',
          backdropFilter: 'blur(10px)',
          transition: 'border-color 0.2s, border-radius 0.15s',
          overflow: 'hidden',
          height: 36,
        }}
      >
        <Search
          size={14}
          style={{ marginLeft: 10, flexShrink: 0, color: 'rgba(255,255,255,0.55)' }}
        />
        <input
          ref={inputRef}
          type="text"
          value={term}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true);
          }}
          placeholder="Rechercher un joueur…"
          autoComplete="off"
          spellCheck={false}
          style={{
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'white',
            fontSize: '0.8rem',
            padding: '0 8px',
            flex: 1,
            minWidth: 0,
          }}
        />
        {isLoading && (
          <span
            className="spinner-border spinner-border-sm me-2"
            style={{ width: 12, height: 12, borderWidth: 2, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}
          />
        )}
        {term && !isLoading && (
          <button
            className="btn btn-sm p-0 me-2 d-flex align-items-center"
            style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}
            onMouseDown={(e) => { e.preventDefault(); handleClear(); }}
            tabIndex={-1}
          >
            <X size={12} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && results.length > 0 && (
        <div
          className="position-absolute w-100"
          style={{
            top: '100%',
            left: 0,
            zIndex: 1100,
            background: 'rgba(15,23,42,0.97)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderTop: 'none',
            borderRadius: '0 0 10px 10px',
            boxShadow: '0 16px 40px rgba(0,0,0,0.6)',
            overflow: 'hidden',
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {selectedLeague ? (
            <>
              {leaguePlayers.length > 0 && (
                <>
                  {renderGroupLabel('Ma ligue')}
                  {leaguePlayers.map((p, i) => renderItem(p, i))}
                </>
              )}
              {freeAgentPlayers.length > 0 && (
                <>
                  {renderGroupLabel('Agents libres')}
                  {freeAgentPlayers.map((p, i) => renderItem(p, leaguePlayers.length + i))}
                </>
              )}
              {leaguePlayers.length === 0 && freeAgentPlayers.length === 0 && (
                <div className="px-3 py-3 text-center" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)' }}>
                  Aucun résultat
                </div>
              )}
            </>
          ) : (
            <>
              {globalPlayers.length > 0
                ? globalPlayers.map((p, i) => renderItem(p, i))
                : (
                  <div className="px-3 py-3 text-center" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)' }}>
                    Aucun résultat
                  </div>
                )}
            </>
          )}
        </div>
      )}

      {/* No results message */}
      {isOpen && !isLoading && term.length >= 3 && results.length === 0 && (
        <div
          className="position-absolute w-100"
          style={{
            top: '100%',
            left: 0,
            zIndex: 1100,
            background: 'rgba(15,23,42,0.97)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderTop: 'none',
            borderRadius: '0 0 10px 10px',
            boxShadow: '0 16px 40px rgba(0,0,0,0.6)',
            padding: '12px 16px',
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.45)',
            textAlign: 'center',
          }}
        >
          Aucun joueur trouvé pour « {term} »
        </div>
      )}
    </div>
  );
}
